import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getSession, clearSession, updateSessionToken } from "@/lib/auth";

// Module-level, shared across every useMerchant() instance mounted around
// the same time (e.g. RequireOwner wrapping a page that also calls
// useMerchant() itself) — so a route like /billing that's both guarded and
// self-fetching only ever fires ONE actual GET /api/me request, not two.
// Cleared once the request settles, so the next real navigation fetches
// fresh data rather than serving something stale.
let inFlightMe = null;

function fetchMeOnce() {
  if (!inFlightMe) {
    inFlightMe = api.get("/me").finally(() => {
      inFlightMe = null;
    });
  }
  return inFlightMe;
}

/**
 * Every merchant-facing page needs the same three checks on mount:
 *   1. bounce to /login if there's no session token
 *   2. fetch /api/me
 *   3. bounce to /plans if the merchant hasn't picked a plan yet
 * This hook does that once. Multiple components mounting it at the same
 * time (e.g. a RequireOwner guard wrapping a page that also uses this
 * hook) share a single in-flight /api/me request rather than each firing
 * their own — see fetchMeOnce() above.
 *
 * Pass { requirePlan: false } for a page that should render even before a
 * plan is chosen (none of the current pages need this yet, but it's here).
 */
export function useMerchant({ requirePlan = true } = {}) {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const s = getSession();
    if (!s?.token) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await fetchMeOnce();
        updateSessionToken(data.session_jwt);
        if (requirePlan && !data.plan_selected) {
          navigate("/plans", { replace: true });
          return;
        }
        setMe(data);
      } catch (err) {
        // Only wipe the session when the server says the token is invalid.
        // A network blip or a 5xx must not log the merchant out.
        const status = err?.response?.status;
        if (status === 401 || status === 403) clearSession();
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, requirePlan]);

  return { me, loading, setMe };
}

/**
 * Same idea as useMerchant(), for pages that are public reference docs
 * (the -guide pages: /tabs-guide, /status-guide, /analytics-guide,
 * /discounts-guide, /abandoned-cart-guide, /team-guide) but still want to
 * show the merchant header/plan-badge/profile-menu when someone happens
 * to be logged in.
 *
 * Unlike useMerchant(), this NEVER calls navigate("/login") — a guide
 * page with no session should just render with me = null, not bounce the
 * visitor away. That matters for two audiences beyond logged-in users:
 * logged-out members/owners who land here from a Google search before
 * ever signing in, and crawlers (Google, AI bots) indexing the content -
 * both would otherwise get redirected to /login before seeing anything,
 * regardless of what NoIndexGuard's robots meta says.
 *
 * DashboardHeader/ProfileMenu already treat `me` as optional (every field
 * access is `me?.x`), so passing me = null renders a generic "Account"
 * header with no plan badge - safe by construction.
 */
export function useOptionalMerchant() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const s = getSession();
    if (!s?.token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await fetchMeOnce();
        updateSessionToken(data.session_jwt);
        setMe(data);
      } catch {
        // Stale/invalid token on a public page - just show it logged-out
        // rather than clearing the session or redirecting anywhere.
        setMe(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { me, loading };
}

/** "growth" -> "Growth". Plan names aren't in the /api/me response yet
 *  (see api-me's plan_meta, currently always {}), so derive the display
 *  label from the plan key itself rather than waiting on that. */
export function planLabel(planKey) {
  if (!planKey) return "";
  return planKey.charAt(0).toUpperCase() + planKey.slice(1);
}

/**
 * True for the business owner (Instagram-OAuth session), false for an
 * invited team member logged in via /team/login or /team/accept.
 *
 * Owner sessions never carry a `role` claim — only team_members rows do —
 * so "no role on /me" means owner. Team member sessions carry whatever's
 * in team_members.role ('member' today, 'manager' or others later).
 *
 * NOTE: this depends on GET /api/me returning `role` straight from the
 * JWT verify output for team-member sessions. If /api/me hasn't been
 * updated to include that field yet, every session will look like an
 * owner here — check that before relying on this for anything sensitive.
 */
export function isOwner(me) {
  return !me?.role || me.role === "owner";
}
